/**
 * Intent Extractor — Parse user's raw prompt into a SceneGraph.
 * Supports Arabic + English. Every unmentioned field stays NULL.
 * RULE: Extract ONLY what the user explicitly stated.
 */

import {
  type SceneGraph,
  createEmptySceneGraph,
} from './sceneGraph';

// ─── Pattern Dictionaries ───

const CHARACTER_PATTERNS: Array<[RegExp, string]> = [
  [/\b(zombie|زومبي|زمبي)\b/i, 'zombie'],
  [/\b(warrior|محارب|فارس|مقاتل)\b/i, 'warrior'],
  [/\b(knight|فارس)\b/i, 'knight'],
  [/\b(dragon|تنين)\b/i, 'dragon'],
  [/\b(robot|روبوت|آلي)\b/i, 'robot'],
  [/\b(cat|kitten|قطة|قطه)\b/i, 'cat'],
  [/\b(dog|puppy|كلب)\b/i, 'dog'],
  [/\b(girl|بنت|فتاة)\b/i, 'girl'],
  [/\b(boy|ولد|صبي)\b/i, 'boy'],
  [/\b(man|رجل|راجل)\b/i, 'man'],
  [/\b(woman|امرأة|ست|سيدة)\b/i, 'woman'],
  [/\b(old man|عجوز|شايب)\b/i, 'old man'],
  [/\b(child|طفل|طفلة)\b/i, 'child'],
  [/\b(samurai|ساموراي)\b/i, 'samurai'],
  [/\b(ninja|نينجا)\b/i, 'ninja'],
  [/\b(witch|ساحرة)\b/i, 'witch'],
  [/\b(wizard|ساحر)\b/i, 'wizard'],
  [/\b(pirate|قرصان)\b/i, 'pirate'],
  [/\b(astronaut|رائد فضاء)\b/i, 'astronaut'],
  [/(?:شخص|شخصية|person|character)\s+(\S+)/i, '$1'],
];

const EMOTION_PATTERNS: Array<[RegExp, string]> = [
  [/\b(laughing|بيضحك|ضاحك|يضحك)\b/i, 'laughing'],
  [/\b(smiling|مبتسم|بيبتسم)\b/i, 'smiling'],
  [/\b(crying|بيعيط|يبكي|باكي)\b/i, 'crying'],
  [/\b(angry|غاضب|زعلان|غضبان)\b/i, 'angry'],
  [/\b(sad|حزين|زعلان)\b/i, 'sad'],
  [/\b(scared|خايف|مرعوب)\b/i, 'scared'],
  [/\b(happy|سعيد|فرحان|مبسوط)\b/i, 'happy'],
  [/\b(menacing|مخيف|مرعب|شرير)\b/i, 'menacing'],
  [/\b(neutral|عادي)\b/i, 'neutral'],
  [/\b(surprised|مندهش|متفاجئ)\b/i, 'surprised'],
  [/\b(confident|واثق)\b/i, 'confident'],
  [/\b(mysterious|غامض)\b/i, 'mysterious'],
];

const CLOTHING_PATTERNS: Array<[RegExp, string]> = [
  [/\b(suit|بدلة)\b/i, 'suit'],
  [/\b(armor|درع)\b/i, 'armor'],
  [/\b(dress|فستان)\b/i, 'dress'],
  [/\b(cloak|عباية|رداء)\b/i, 'cloak'],
  [/\b(uniform|زي)\b/i, 'uniform'],
  [/\b(hoodie|هودي)\b/i, 'hoodie'],
  [/\b(leather jacket|جاكيت جلد)\b/i, 'leather jacket'],
  [/\b(robe|جلباب)\b/i, 'robe'],
];

const POSE_PATTERNS: Array<[RegExp, string]> = [
  [/\b(standing|واقف|واقفة)\b/i, 'standing'],
  [/\b(sitting|جالس|قاعد)\b/i, 'sitting'],
  [/\b(walking|ماشي|بيمشي)\b/i, 'walking'],
  [/\b(running|بيجري|جاري)\b/i, 'running'],
  [/\b(fighting|بيحارب|بيقاتل)\b/i, 'fighting'],
  [/\b(dancing|بيرقص|راقص)\b/i, 'dancing'],
  [/\b(kneeling|راكع)\b/i, 'kneeling'],
  [/\b(lying|نايم|راقد)\b/i, 'lying down'],
  [/\b(flying|طاير|بيطير)\b/i, 'flying'],
];

const DISTANCE_PATTERNS: Array<[RegExp, 'close' | 'medium' | 'far']> = [
  [/\b(من بعيد|بعيد|far away|from a?far|distant|from a distance|long shot)\b/i, 'far'],
  [/\b(من قريب|قريب|close[- ]?up|وجه|face)\b/i, 'close'],
  [/\b(medium|متوسط)\b/i, 'medium'],
];

const ANGLE_PATTERNS: Array<[RegExp, string]> = [
  [/\b(low angle|من تحت|زاوية منخفضة)\b/i, 'low_angle'],
  [/\b(high angle|من فوق|زاوية عالية)\b/i, 'high_angle'],
  [/\b(top[- ]?down|bird'?s?[- ]?eye|overhead|من أعلى)\b/i, 'birds_eye'],
  [/\b(eye[- ]?level|مستوى العين)\b/i, 'eye_level'],
  [/\b(dutch angle|مائل)\b/i, 'dutch_angle'],
];

const ENVIRONMENT_PATTERNS: Array<[RegExp, string]> = [
  [/\b(forest|غابة)\b/i, 'dark forest'],
  [/\b(desert|صحراء)\b/i, 'vast desert'],
  [/\b(city|مدينة)\b/i, 'urban city'],
  [/\b(temple|معبد)\b/i, 'ancient temple'],
  [/\b(underwater|تحت الماء)\b/i, 'underwater'],
  [/\b(space|فضاء)\b/i, 'outer space'],
  [/\b(mountain|جبل)\b/i, 'mountain landscape'],
  [/\b(beach|شاطئ|بحر)\b/i, 'beach shore'],
  [/\b(street|شارع)\b/i, 'street'],
  [/\b(castle|قلعة)\b/i, 'medieval castle'],
  [/\b(graveyard|مقبرة)\b/i, 'graveyard'],
  [/\b(lab|مختبر|laboratory)\b/i, 'laboratory'],
  [/\b(cave|كهف)\b/i, 'dark cave'],
  [/\b(ruins|أطلال|خرابة)\b/i, 'ancient ruins'],
];

const TIME_PATTERNS: Array<[RegExp, string]> = [
  [/\b(night|ليل|بالليل)\b/i, 'night'],
  [/\b(golden hour|ساعة ذهبية|غروب)\b/i, 'golden_hour'],
  [/\b(sunrise|شروق)\b/i, 'sunrise'],
  [/\b(sunset|غروب الشمس)\b/i, 'sunset'],
  [/\b(morning|صباح|الصبح)\b/i, 'morning'],
  [/\b(noon|ظهر)\b/i, 'noon'],
  [/\b(blue hour)\b/i, 'blue_hour'],
  [/\b(dawn|فجر)\b/i, 'dawn'],
  [/\b(midnight|نص الليل)\b/i, 'midnight'],
];

const WEATHER_PATTERNS: Array<[RegExp, string]> = [
  [/\b(rain|مطر|بيمطر)\b/i, 'rain'],
  [/\b(snow|ثلج|تلج)\b/i, 'snow'],
  [/\b(fog|ضباب|شبورة)\b/i, 'fog'],
  [/\b(storm|عاصفة|رعد)\b/i, 'storm'],
  [/\b(wind|رياح|هوا)\b/i, 'wind'],
];

const GENRE_PATTERNS: Array<[RegExp, string]> = [
  [/\b(horror|رعب|مرعب)\b/i, 'horror'],
  [/\b(epic|ملحمي|ملحمة)\b/i, 'epic'],
  [/\b(romantic|رومانسي)\b/i, 'romantic'],
  [/\b(sci[- ]?fi|خيال علمي)\b/i, 'sci-fi'],
  [/\b(fantasy|خيالي|فانتازيا)\b/i, 'fantasy'],
  [/\b(noir|نوار)\b/i, 'noir'],
  [/\b(action|أكشن)\b/i, 'action'],
  [/\b(comedy|كوميدي)\b/i, 'comedy'],
  [/\b(mystery|غموض)\b/i, 'mystery'],
  [/\b(war|حرب)\b/i, 'war'],
];

const RENDER_STYLE_PATTERNS: Array<[RegExp, string]> = [
  [/\b(photorealistic|واقعي)\b/i, 'photorealistic'],
  [/\b(cinematic|سينمائي)\b/i, 'cinematic'],
  [/\b(anime|انمي|أنمي)\b/i, 'anime'],
  [/\b(oil painting|لوحة زيتية)\b/i, 'oil painting'],
  [/\b(3d render|ثلاثي الأبعاد)\b/i, '3d render'],
  [/\b(cartoon|كارتون)\b/i, 'cartoon'],
  [/\b(watercolor|ألوان مائية)\b/i, 'watercolor'],
  [/\b(pixel art|بيكسل)\b/i, 'pixel art'],
];

const MOOD_PATTERNS: Array<[RegExp, string]> = [
  [/\b(dramatic|درامي|دراماتيكي)\b/i, 'dramatic'],
  [/\b(eerie|مخيف|مقلق)\b/i, 'eerie'],
  [/\b(peaceful|هادئ|سلام)\b/i, 'peaceful'],
  [/\b(tense|متوتر|توتر)\b/i, 'tense'],
  [/\b(dark|مظلم)\b/i, 'dark'],
  [/\b(mystical|صوفي|غامض)\b/i, 'mystical'],
  [/\b(heroic|بطولي)\b/i, 'heroic'],
  [/\b(melancholic|حزين)\b/i, 'melancholic'],
  [/\b(warm|دافئ)\b/i, 'warm'],
  [/\b(cold|بارد)\b/i, 'cold'],
];

const LIGHTING_TYPE_PATTERNS: Array<[RegExp, string]> = [
  [/\b(dramatic light|إضاءة درامية)\b/i, 'dramatic'],
  [/\b(soft light|إضاءة ناعمة)\b/i, 'soft'],
  [/\b(neon|نيون)\b/i, 'neon'],
  [/\b(candlelight|شموع)\b/i, 'candlelight'],
  [/\b(moonlight|ضوء القمر)\b/i, 'moonlight'],
  [/\b(sunlight|ضوء الشمس)\b/i, 'sunlight'],
  [/\b(backlight|إضاءة خلفية)\b/i, 'backlight'],
  [/\b(spotlight|بقعة ضوء)\b/i, 'spotlight'],
  [/\b(volumetric|حجمي)\b/i, 'volumetric'],
];

// ─── Extraction Logic ───

function firstPatternMatch<T>(text: string, patterns: Array<[RegExp, T]>): T | null {
  for (const [regex, value] of patterns) {
    if (regex.test(text)) {
      // Handle $1 replacement for dynamic capture groups
      if (typeof value === 'string' && value === '$1') {
        const match = text.match(regex);
        return (match?.[1]?.trim() || null) as T;
      }
      return value;
    }
  }
  return null;
}

/**
 * Extract user intent from raw prompt → SceneGraph with NULLs for unmentioned fields.
 * This is Layer 2: Semantic Analysis.
 */
export function extractIntent(rawPrompt: string): SceneGraph {
  const text = rawPrompt.trim();
  if (!text) return createEmptySceneGraph(rawPrompt);

  const graph = createEmptySceneGraph(rawPrompt);
  const specified: string[] = [];

  // ── Character extraction ──
  const charType = firstPatternMatch(text, CHARACTER_PATTERNS);
  if (charType) { graph.character.type = charType; specified.push('character.type'); }

  const emotion = firstPatternMatch(text, EMOTION_PATTERNS);
  if (emotion) { graph.character.emotion = emotion; specified.push('character.emotion'); }

  const clothing = firstPatternMatch(text, CLOTHING_PATTERNS);
  if (clothing) { graph.character.clothing = clothing; specified.push('character.clothing'); }

  const pose = firstPatternMatch(text, POSE_PATTERNS);
  if (pose) { graph.character.pose = pose; specified.push('character.pose'); }

  // If emotion implies action
  if (emotion && !pose) {
    graph.character.action = emotion === 'laughing' ? 'laughing expressively'
      : emotion === 'crying' ? 'crying with emotion'
      : emotion === 'fighting' ? 'in combat stance'
      : null;
    if (graph.character.action) specified.push('character.action');
  }

  // ── Camera extraction ──
  const distance = firstPatternMatch(text, DISTANCE_PATTERNS);
  if (distance) { graph.camera.distance = distance; specified.push('camera.distance'); }

  const angle = firstPatternMatch(text, ANGLE_PATTERNS);
  if (angle) { graph.camera.angle = angle; specified.push('camera.angle'); }

  // Explicit shot type from text
  if (/\b(wide shot|لقطة واسعة)\b/i.test(text)) {
    graph.camera.shotType = 'wide'; specified.push('camera.shotType');
  } else if (/\b(close[- ]?up|لقطة قريبة)\b/i.test(text)) {
    graph.camera.shotType = 'close_up'; specified.push('camera.shotType');
  } else if (/\b(medium shot|لقطة متوسطة)\b/i.test(text)) {
    graph.camera.shotType = 'medium'; specified.push('camera.shotType');
  }

  // Explicit lens from text
  const lensMatch = text.match(/\b(\d+)\s*mm\b/i);
  if (lensMatch) {
    graph.camera.lens = `${lensMatch[1]}mm`; specified.push('camera.lens');
  }

  // ── Environment extraction ──
  const location = firstPatternMatch(text, ENVIRONMENT_PATTERNS);
  if (location) { graph.environment.location = location; specified.push('environment.location'); }

  const time = firstPatternMatch(text, TIME_PATTERNS);
  if (time) { graph.environment.time = time; specified.push('environment.time'); }

  const weather = firstPatternMatch(text, WEATHER_PATTERNS);
  if (weather) { graph.environment.weather = weather; specified.push('environment.weather'); }

  // ── Lighting extraction ──
  const lightType = firstPatternMatch(text, LIGHTING_TYPE_PATTERNS);
  if (lightType) { graph.lighting.type = lightType; specified.push('lighting.type'); }

  // ── Style extraction ──
  const genre = firstPatternMatch(text, GENRE_PATTERNS);
  if (genre) { graph.style.genre = genre; specified.push('style.genre'); }

  const renderStyle = firstPatternMatch(text, RENDER_STYLE_PATTERNS);
  if (renderStyle) { graph.style.renderStyle = renderStyle; specified.push('style.renderStyle'); }

  const mood = firstPatternMatch(text, MOOD_PATTERNS);
  if (mood) { graph.style.mood = mood; specified.push('style.mood'); }

  // ── Also try to extract subject from "تصميم لـ" pattern ──
  const designMatch = text.match(/(?:تصميم\s*ل[يـ]?\s*|design\s+(?:of|for)\s+)(.+?)(?:[,.]|$)/i);
  if (designMatch && !charType) {
    graph.character.type = designMatch[1].trim();
    specified.push('character.type');
  }

  // ── Confidence scoring ──
  const totalFields = 20; // approximate total extractable fields
  graph.meta.confidence = Math.min(1, specified.length / (totalFields * 0.5));
  graph.meta.userSpecifiedFields = specified;

  return graph;
}
